import {
  Icon24InfoResponseTime,
  Icon48BrandCalendarOneMonth,
  Icon48BrandSearch,
  Icon24UtilityChevronRight,
} from '@care/react-icons';
import { Box, Container, Typography, Link, Theme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import sanitizeHtml from 'sanitize-html';
import he from 'he';
import { SxClassProps } from '@/types/common';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';

const classes: SxClassProps = {
  icon: {
    width: {
      xs: '48px',
      md: '64px',
    },
    height: {
      xs: '48px',
      md: '64px',
    },
  },
  headline: {
    fontSize: {
      xs: 'h2.fontSize',
      md: 'display3.fontSize',
    },
    lineHeight: {
      xs: '26px',
      md: '42px',
    },
    fontWeight: 'bold',
  },
  seolink: (theme: Theme) => ({
    color: theme.palette.care.grey[900],
    '&:hover': {
      color: theme.palette.care.navy?.[600],
    },
  }),
};

type WhenDoYouWantAJobProps = {
  enrollmentURL?: string;
  slotProps?: {
    icons?: {
      [key: string]: { 'data-testid': string };
    };
  };
};

const WhenDoYouWantAJob = ({ enrollmentURL, slotProps }: WhenDoYouWantAJobProps) => {
  const rightNow = {
    key: 'rightNow',
    label: 'Right now',
    intent: 'rightNow',
    icon: <Icon24InfoResponseTime sx={classes.icon} {...slotProps?.icons?.responseTime} />,
  };

  const withInMonth = {
    key: 'withInMonth',
    label: 'Within a month',
    intent: 'withInMonth',
    icon: <Icon48BrandCalendarOneMonth sx={classes.icon} {...slotProps?.icons?.calendar} />,
  };

  const justBrowsing = {
    key: 'justBrowsing',
    label: 'Just browsing',
    intent: 'justBrowsing',
    icon: <Icon48BrandSearch sx={classes.icon} {...slotProps?.icons?.search} />,
  };

  const headline = 'When do you want a job?';
  const content = [rightNow, withInMonth, justBrowsing];

  const isMobileOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const handleCareButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const amplitudeData = {
      cta_location: 'header',
      cta_text: (e.target as HTMLElement).textContent?.toString(),
      cta_type: 'button',
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: {
          xs: 4,
          sm: 0,
        },
        mb: {
          xs: 1,
          sm: 3,
        },
        mx: {
          xs: 1,
          md: 'auto',
        },
        px: {
          xs: 1,
          md: 10,
        },
        py: {
          xs: 1,
          md: 2,
        },
        maxWidth: {
          md: 'calc(100vw - 5%)',
          lg: '940px',
        },
      }}>
      <Box display="flex" justifyContent={{ xs: 'left', md: 'center' }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{ pb: { xs: 1, md: 4 }, pr: { xs: 1, md: 0 }, maxWidth: { md: '780px' } }}>
          <Typography
            component="span"
            sx={{
              m: 0,
              display: 'inline',
              ...classes.headline,
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(he.decode(headline)),
            }}
          />
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection={{
          xs: 'column',
          md: 'row',
        }}
        justifyContent={{
          xs: 'center',
          md: 'space-between',
        }}>
        {content.map(({ key, label, intent, icon }) => (
          <Link
            key={key}
            sx={classes.seolink}
            href={`${enrollmentURL}&intent=${intent}`}
            underline="none"
            onClick={handleCareButton}>
            <Box
              sx={{
                borderRadius: 4,
                boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.15)',
                px: {
                  xs: 3,
                  md: 'auto',
                },
              }}
              minHeight={{
                md: '154px',
              }}
              key={key}
              pb={{ xs: 2, md: 0 }}
              pt={{ xs: 2, md: 3 }}
              mt={{ xs: 2, md: 0 }}
              display="flex"
              alignItems="center"
              flexDirection={{ md: 'column' }}
              minWidth={{
                md: '240px',
              }}
              px={{
                md: 3,
              }}>
              <Box pr={{ xs: 3.5, md: 0 }}>{icon}</Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width={{
                  xs: '85%',
                  md: 'auto',
                }}
                flexDirection={{ md: 'row' }}>
                <Box flex="1">
                  <Typography
                    variant={isMobileOrSmaller ? 'h3' : 'h2'}
                    textAlign={{ md: 'center' }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(he.decode(label)),
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-end" alignItems="center">
                  <Icon24UtilityChevronRight
                    size={isMobileOrSmaller ? 23 : 25}
                    {...slotProps?.icons?.chevron}
                  />
                </Box>
              </Box>
            </Box>
          </Link>
        ))}
      </Box>
    </Container>
  );
};

export { WhenDoYouWantAJob };
